import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { TimelinePost } from '@/types/post';

interface UserInfoProps {
  post: TimelinePost
}

function formatPostDate(createdAt: string | undefined): string {
  if (!createdAt) return ''
  
  const postDate = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - postDate.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)
  
  if (diffHours < 24) {
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    if (diffMinutes < 60) {
      return `há ${diffMinutes}min`
    }
    return `há ${Math.floor(diffHours)}h`
  }
  
  return postDate.toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  })
}

function getSourceName(source: string | undefined): string {
  if (!source) return 'Culture Monitor'
  
  // Normalizar nomes de fonte
  const sourceLower = source.toLowerCase()
  if (sourceLower.includes('twitter') || sourceLower.includes('x.com') || sourceLower === 'x') {
    return 'X'
  }
  if (sourceLower.includes('google news')) {
    return 'Google News'
  }
  if (sourceLower.includes('google trends')) {
    return 'Google Trends'
  }
  
  // Retornar apenas o nome principal se houver "for" ou similar
  const parts = source.split(' for ')
  return parts[0].trim()
}

export function UserInfo({ post }: UserInfoProps) {
  const name = post.properties?.author?.name || post.metadata?.author?.name || post.metadata?.author?.userName || 'Usuário'
  const username = post.properties?.author?.userName || post.metadata?.author?.userName || post.metadata?.author?.name || ''
  const avatar = post.metadata?.author?.profilePicture || post.properties?.author?.picture || post.metadata?.author?.picture || undefined
  const sourceName = getSourceName(post.metadata?.source || post.source?.name)
  const createdAt = post.createdAt || post.properties?.createdAt || post.metadata?.createdAt || post.created_at
  const formattedDate = formatPostDate(createdAt)
  const isLessThan24h = createdAt ? (new Date().getTime() - new Date(createdAt).getTime()) < 24 * 60 * 60 * 1000 : false

  const handleAvatarError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const fallback = '/src/assets/images/Profile.svg'
    if (e.currentTarget.src !== window.location.origin + fallback && !e.currentTarget.src.endsWith(fallback)) {
      e.currentTarget.src = fallback
    }
  }

  return (
    <div className="flex items-center gap-3">
      <Avatar className="w-10 h-10">
        <AvatarImage
          src={avatar || '/src/assets/images/Profile.svg'}
          onError={handleAvatarError}
        />
        <AvatarFallback>{name[0]}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col">
        <span className="font-semibold text-base">{name}</span>
        {username && (
          <span className="text-sm text-gray-500">@{username}</span>
        )}
        {formattedDate && (
          <span className="text-sm text-gray-500">
            {isLessThan24h ? `Postado ${formattedDate}` : `Postado em ${formattedDate}`} • {sourceName}
          </span>
        )}
      </div>
    </div>
  )
}
